import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { GoalDto, HabitInput, PlanProposal } from "@habitos/shared";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAiStatus, useGoals, useHabitStats, useHabits } from "../lib/queries";
import { StreakLine } from "./Habits";
import { RecentProgress } from "./Review";
import { describeDays, formatDay, formatDuration } from "../lib/format";
import { PlanReview, Thinking } from "../components/PlanReview";

export function GoalDetail() {
  const { id } = useParams();
  const goals = useGoals();
  const goal = goals.data?.find((g) => g.id === id);

  if (goals.isPending) return <p className="muted center">Cargando…</p>;
  if (!goal) return <p className="muted center">Esta meta no existe.</p>;
  return <GoalView key={goal.id} goal={goal} />;
}

function GoalView({ goal }: { goal: GoalDto }) {
  const { today } = useAuth();
  const navigate = useNavigate();
  const client = useQueryClient();
  const habits = useHabits();
  const stats = useHabitStats();
  const ai = useAiStatus();
  const [replanning, setReplanning] = useState(goal.progress.needsReplan);
  const [note, setNote] = useState("");
  const [proposal, setProposal] = useState<PlanProposal | null>(null);
  const [thinking, setThinking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = (habits.data ?? []).filter(
    (h) => goal.habitIds.includes(h.id) && (!h.endDate || h.endDate >= today),
  );
  const { progress } = goal;

  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["goals"] }),
      client.invalidateQueries({ queryKey: ["habits"] }),
    ]);

  async function propose() {
    setError(null);
    setThinking(true);
    setProposal(null);
    try {
      setProposal(await api.proposeReplan(goal.id, note.trim() || null));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setThinking(false);
    }
  }

  async function accept(habits: HabitInput[]) {
    if (!proposal) return;
    setSaving(true);
    try {
      await api.applyPlan(goal.id, { planSummary: proposal.summary, habits });
      await refresh();
      setProposal(null);
      setReplanning(false);
      setNote("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function archive() {
    if (!confirm("¿Archivar esta meta? Sus pasos dejarán de aparecer y de recordarte.")) return;
    await api.archiveGoal(goal.id, true);
    await refresh();
    navigate("/metas");
  }

  if (thinking) return <Thinking text="Revisando cómo te ha ido y buscando un plan más fácil de sostener…" />;

  if (proposal) {
    return (
      <>
        <header className="page-header row start">
          <button className="icon-btn" aria-label="Volver" onClick={() => setProposal(null)}>‹</button>
          <h1>Nuevo plan</h1>
        </header>
        <PlanReview
          proposal={proposal}
          acceptLabel="Usar este plan desde hoy"
          busy={saving}
          onAccept={accept}
          onRetry={() => void propose()}
        />
        {error && <p className="error">{error}</p>}
      </>
    );
  }

  return (
    <>
      <header className="page-header row start">
        <button className="icon-btn" aria-label="Volver" onClick={() => navigate("/metas")}>‹</button>
        <h1>{goal.title}</h1>
      </header>

      <div className="stack">
        {goal.planSummary && (
          <section className="card" style={{ borderLeft: `5px solid ${goal.color}` }}>
            <p className="summary">{goal.planSummary}</p>
          </section>
        )}

        {progress.scheduled > 0 && (
          <div className="progress" role="progressbar" aria-valuenow={progress.done} aria-valuemax={progress.scheduled}>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${(progress.done / progress.scheduled) * 100}%` }} />
            </div>
            <span>{progress.done} de {progress.scheduled} esta semana</span>
          </div>
        )}
        {goal.recent.scheduled > 0 && (
          <div className="stack-tight">
            <p className="muted small">Últimas 4 semanas: {goal.recent.done} de {goal.recent.scheduled} pasos hechos</p>
            <RecentProgress done={goal.recent.done} scheduled={goal.recent.scheduled} />
          </div>
        )}

        <h2 className="section">Pasos</h2>
        {steps.length === 0 ? (
          <p className="muted">Esta meta no tiene pasos activos.</p>
        ) : (
          <ul className="list">
            {steps.map((h) => (
              <li key={h.id}>
                <Link to={`/habitos/${h.id}`} className="habit card" style={{ borderLeftColor: h.color }}>
                  <div className="habit-time">{h.time}</div>
                  <div className="habit-body">
                    <div className="habit-title">{h.emoji} {h.title}</div>
                    <div className="muted small">
                      {describeDays(h.daysOfWeek)} · {formatDuration(h.durationMinutes)}
                    </div>
                    <div className="muted small">
                      {h.startDate > today ? `Empieza ${formatDay(h.startDate)}` : "En curso"}
                      {h.endDate ? ` · hasta ${formatDay(h.endDate)}` : ""}
                    </div>
                    <StreakLine streak={stats.data?.find((s) => s.habitId === h.id)} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {ai.data?.enabled && (
          <section className="card form">
            {progress.needsReplan ? (
              <p className="summary">
                Varios pasos se quedaron sin hacer esta semana. Pasa, y es una pista de que el plan pide
                demasiado. ¿Lo ajustamos?
              </p>
            ) : (
              !replanning && (
                <button className="btn" onClick={() => setReplanning(true)}>Ajustar el plan con IA</button>
              )
            )}
            {replanning && (
              <>
                <label>
                  ¿Qué se interpuso? <span className="muted">(opcional)</span>
                  <textarea
                    className="textarea"
                    maxLength={500}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Las mañanas son un caos; los martes no puedo"
                  />
                </label>
                <button className="btn primary" onClick={() => void propose()}>Proponer un plan más fácil</button>
              </>
            )}
            {error && <p className="error">{error}</p>}
          </section>
        )}

        <div className="row gap danger-zone">
          <button className="btn" onClick={archive}>Archivar meta</button>
        </div>
      </div>
    </>
  );
}
